import React, { FC, useState, useEffect, useCallback, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { KTIcon } from '../../../_metronic/helpers';
import { useThemeMode } from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';
import { useAuth } from '../../modules/auth';
import { getClients } from '../../modules/apps/clients/core/_requests';
import { getDashboardSummary } from './core/_requests';
import { DashboardSummaryDto } from './core/_models';
import { AppSelect } from '../../modules/widgets/components/AppSelect';
import { formatMillions, formatShortIdr } from '../../../_metronic/helpers'
import '../../../_metronic/assets/sass/dashboard-billing.scss';
import '../../../_metronic/assets/sass/BillingStyles.scss';

type ViewMode = 'SPLIT' | 'CHART' | 'DATA';

// Formatting Helpers

const formatCalls = (calls?: number): string => {
  if (!calls) return '0';
  if (calls >= 1_000_000) return `${(calls / 1_000_000).toFixed(1)}M`;
  if (calls >= 1_000) return `${(calls / 1_000).toFixed(1)}k`;
  return calls.toLocaleString('id-ID');
};

const formatCompactMinutes = (mins?: number): string => {
  if (!mins) return '0 Mins';
  if (mins >= 1_000_000) return `${(mins / 1_000_000).toFixed(2)}M Mins`;
  if (mins >= 1_000) return `${(mins / 1_000).toFixed(1)}k Mins`;
  return `${Math.round(mins).toLocaleString('id-ID')} Mins`;
};

const formatNumber = (val?: number): string => {
  return (val || 0).toLocaleString('id-ID');
};

const getAvatarClass = (idx: number): string => {
  const classes = ['avatar-navy', 'avatar-slate', 'avatar-midnight', 'avatar-blue'];
  return classes[idx % classes.length];
};

const DashboardBilling: FC = () => {
  const { mode } = useThemeMode();
  const isDark =
    mode === 'dark' ||
    (typeof document !== 'undefined' &&
      document.documentElement.getAttribute('data-bs-theme') === 'dark');

  const { currentUser } = useAuth();
  const roleName = currentUser?.role?.name?.toUpperCase();
  const isAdmin = (roleName === 'ADMIN' || roleName === 'SUPERADMIN') && !currentUser?.clientId;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardSummaryDto | null>(null);

  // Filters
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [clients, setClients] = useState<any[]>([]);

  const clientOptions = useMemo(() => [
    { value: '', label: 'Semua Klien (All)' },
    ...clients.map((c) => ({ value: c.id, label: c.clientName }))
  ], [clients]);
  const [startDate, setStartDate] = useState<string>(() => moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string>(() => moment().endOf('month').format('YYYY-MM-DD'));
  const [invoicePeriod, setInvoicePeriod] = useState<string>(() => moment().format('YYYY-MM'));
  const [activeInvoiceFilter, setActiveInvoiceFilter] = useState(() => moment().format('MMMM YYYY'));

  // Interactive View Toggles
  const [dashboardScope, setDashboardScope] = useState<'ALL' | 'PRODUCTS' | 'CDR'>('ALL');
  const [comparisonView, setComparisonView] = useState<ViewMode>('SPLIT');
  const [providerView, setProviderView] = useState<ViewMode>('SPLIT');
  const [destinationView, setDestinationView] = useState<'SUMMARY' | 'DATA'>('SUMMARY');
  const [productAdoptionView, setProductAdoptionView] = useState<ViewMode>('SPLIT');
  const [contractFilter, setContractFilter] = useState<'ALL' | 'SUBSCRIPTION' | 'ONE_TIME' | 'OVERRIDDEN'>('ALL');

  // Load clients list for Admin
  useEffect(() => {
    if (isAdmin) {
      getClients()
        .then((res: any) => {
          if (res?.data && Array.isArray(res.data)) {
            setClients(res.data);
          }
        })
        .catch((err) => console.error('Failed to load clients:', err));
    }
  }, [isAdmin]);

  // Fetch Dashboard Summary Data (including synchronized invoiceSettlement from /call/dashboard/summary)
  const fetchDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      const targetClientId = selectedClientId
        ? Number(selectedClientId)
        : !isAdmin
          ? currentUser?.clientId || undefined
          : undefined;

      const res = await getDashboardSummary({
        startDate,
        endDate,
        clientId: targetClientId,
        invoicePeriod,
      });

      setDashboardData(res);
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [startDate, endDate, selectedClientId, invoicePeriod, isAdmin, currentUser?.clientId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleInvoiceMonthChange = (date: Date | null) => {
    if (date) {
      const s = moment(date).startOf('month').format('YYYY-MM-DD');
      const e = moment(date).endOf('month').format('YYYY-MM-DD');
      const ym = moment(date).format('YYYY-MM');
      setStartDate(s);
      setEndDate(e);
      setInvoicePeriod(ym);
      setActiveInvoiceFilter(moment(date).format('MMMM YYYY'));
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchDashboardData();
  };

  // ==========================================
  // 1. DAILY COMPARISON CONFIG & SERIES (WITHIN SELECTED BILLING MONTH)
  // ==========================================
  const monthlyComparisonData = useMemo(() => {
    return dashboardData?.monthlyComparison || [];
  }, [dashboardData]);

  const monthlyCategories = useMemo(() => {
    return monthlyComparisonData.length > 0
      ? monthlyComparisonData.map((m) => m.month)
      : ['Tidak Ada Data'];
  }, [monthlyComparisonData]);

  const comparisonBarSeries = useMemo(() => {
    return [
      {
        name: 'Smart Billing',
        data: monthlyComparisonData.map((m) => Math.round(m.smartCost || 0)),
      },
      {
        name: 'Standar Telkom',
        data: monthlyComparisonData.map((m) => Math.round(m.telkomCost || 0)),
      },
    ];
  }, [monthlyComparisonData]);

  const comparisonBarOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        fontFamily: 'Inter, Outfit, sans-serif',
        toolbar: { show: false },
        background: 'transparent',
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '45%',
          borderRadius: 4,
        },
      },
      colors: isDark ? ['#FAF7EE', '#C49B5A'] : ['#163A6B', '#D97706'],
      dataLabels: { enabled: false },
      stroke: { show: true, width: 2, colors: ['transparent'] },
      xaxis: {
        categories: monthlyCategories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: { colors: isDark ? '#D4CEBF' : '#486581', fontWeight: 600, fontSize: '10px' },
        },
      },
      yaxis: {
        labels: {
          formatter: (val) => formatShortIdr(val),
          style: { colors: isDark ? '#D4CEBF' : '#486581', fontWeight: 600, fontSize: '10px' },
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        labels: { colors: isDark ? '#FAF7EE' : '#163A6B' },
      },
      grid: {
        borderColor: isDark ? 'rgba(250, 247, 238, 0.08)' : 'rgba(44, 81, 103, 0.12)',
        strokeDashArray: 4,
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        y: { formatter: (val) => `Rp ${Math.round(val).toLocaleString('id-ID')}` },
      },
    }),
    [isDark, monthlyCategories]
  );

  const monthlyAreaSeries = comparisonBarSeries;

  const monthlyAreaOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'area',
        fontFamily: 'Inter, Outfit, sans-serif',
        toolbar: { show: false },
        background: 'transparent',
        sparkline: { enabled: false },
      },
      colors: isDark ? ['#FAF7EE', '#C49B5A'] : ['#163A6B', '#D97706'],
      dataLabels: { enabled: false },
      markers: {
        size: 4,
        strokeWidth: 2,
        hover: { size: 6 },
      },
      stroke: { curve: 'smooth', width: 2.5 },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.05,
          stops: [0, 95, 100],
        },
      },
      xaxis: {
        categories: monthlyCategories,
        labels: {
          style: { colors: isDark ? '#9FB3C8' : '#64748B', fontSize: '10px', fontWeight: 600 },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          formatter: (val) => formatShortIdr(val),
          style: { colors: isDark ? '#9FB3C8' : '#64748B', fontSize: '10px' },
        },
      },
      grid: {
        borderColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)',
        strokeDashArray: 4,
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        labels: { colors: isDark ? '#FAF7EE' : '#0A1428' },
        fontSize: '11px',
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        y: { formatter: (val) => `Rp ${Math.round(val).toLocaleString('id-ID')}` },
      },
    }),
    [isDark, monthlyCategories]
  );

  // ==========================================
  // 2. PROVIDER DISTRIBUTION CONFIG & SERIES
  // ==========================================
  const providerData = useMemo(() => {
    return dashboardData?.providerDistribution || [];
  }, [dashboardData]);

  const providerLabels = useMemo(() => {
    return providerData.length > 0 ? providerData.map((p) => p.name) : ['Belum Ada Provider'];
  }, [providerData]);

  const providerColors = useMemo(() => {
    return providerData.length > 0 ? providerData.map((p) => p.color) : ['#163A6B'];
  }, [providerData]);

  const providerDonutSeries = useMemo(() => {
    return providerData.length > 0 ? providerData.map((p) => p.porsi) : [100];
  }, [providerData]);

  const totalCallsFormatted = useMemo(() => {
    return dashboardData?.kpi.totalCalls ? dashboardData.kpi.totalCalls.toLocaleString('id-ID') : '0';
  }, [dashboardData]);

  const providerDonutOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'donut',
        fontFamily: 'Inter, Outfit, sans-serif',
        background: 'transparent',
      },
      colors: providerColors,
      labels: providerLabels,
      dataLabels: { enabled: false },
      stroke: { show: true, width: 2, colors: [isDark ? '#0D1E3D' : '#FFFDF9'] },
      legend: { show: false },
      plotOptions: {
        pie: {
          donut: {
            size: '74%',
            labels: {
              show: true,
              total: {
                show: true,
                showAlways: true,
                label: 'Total Calls',
                fontSize: '9.5px',
                fontWeight: 600,
                color: isDark ? '#9FB3C8' : '#64748B',
                formatter: () => totalCallsFormatted,
              },
              value: {
                fontSize: '15px',
                fontWeight: 800,
                offsetY: 4,
                color: isDark ? '#FAF7EE' : '#0A1428',
              },
            },
          },
        },
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        y: { formatter: (val) => `${val}% Porsi Volume` },
      },
    }),
    [isDark, providerColors, providerLabels, totalCallsFormatted]
  );

  const providerPolarSeries = providerDonutSeries;

  const providerPolarOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'polarArea',
        fontFamily: 'Inter, Outfit, sans-serif',
        background: 'transparent',
        toolbar: { show: false },
      },
      labels: providerLabels,
      colors: providerColors,
      stroke: {
        show: true,
        width: 2,
        colors: [isDark ? '#0D1E3D' : '#FFFDF9'],
      },
      fill: { opacity: 0.88 },
      yaxis: { show: false },
      legend: {
        show: true,
        position: 'bottom',
        labels: { colors: isDark ? '#FAF7EE' : '#0A1428' },
      },
      plotOptions: {
        polarArea: {
          rings: {
            strokeWidth: 1,
            strokeColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
          },
          spokes: {
            strokeWidth: 1,
            connectorColors: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
          },
        },
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        y: { formatter: (val) => `${val}% Porsi Traffic` },
      },
    }),
    [isDark, providerColors, providerLabels]
  );

  // ==========================================
  // 3. DESTINATION BREAKDOWN
  // ==========================================
  const destinationData = useMemo(() => {
    return dashboardData?.destinationDistribution || [];
  }, [dashboardData]);

  // ==========================================
  // 4. PEAK TRAFFIC 24 HOURS
  // ==========================================
  const peakTrafficList = useMemo(() => {
    return dashboardData?.peakTraffic || [];
  }, [dashboardData]);

  const peakTrafficHours = useMemo(() => {
    return peakTrafficList.map((p) => p.hourLabel);
  }, [peakTrafficList]);

  const peakTrafficSeries = useMemo(() => {
    return [
      {
        name: 'Volume Panggilan',
        data: peakTrafficList.map((p) => p.calls),
      },
    ];
  }, [peakTrafficList]);

  const peakTrafficOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'area',
        toolbar: { show: false },
        background: 'transparent',
        sparkline: { enabled: false },
      },
      dataLabels: { enabled: false },
      markers: {
        size: 3,
        colors: isDark ? ['#FAF7EE'] : ['#163A6B'],
        strokeColors: isDark ? '#0A1428' : '#FFFDF9',
        strokeWidth: 2,
        hover: { size: 5 },
      },
      stroke: { curve: 'smooth', width: 2.5 },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: isDark ? 0.45 : 0.35,
          opacityTo: 0.05,
          stops: [0, 95, 100],
        },
      },
      colors: isDark ? ['#FAF7EE'] : ['#163A6B'],
      xaxis: {
        categories: peakTrafficHours,
        labels: {
          style: {
            colors: isDark ? '#9FB3C8' : '#64748B',
            fontSize: '10px',
            fontWeight: 600,
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: {
            colors: isDark ? '#9FB3C8' : '#64748B',
            fontSize: '10px',
          },
          formatter: (val: number) => `${val}`,
        },
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        y: { formatter: (val: number) => `${val.toLocaleString('id-ID')} Panggilan` },
      },
      grid: {
        borderColor: isDark ? 'rgba(255, 255, 255, 0.07)' : 'rgba(44, 81, 103, 0.08)',
        strokeDashArray: 3,
      },
    }),
    [isDark, peakTrafficHours]
  );

  // Peak analysis highlights
  const peakMorning = useMemo(() => {
    const morningHours = peakTrafficList.filter((p) => p.hour >= 6 && p.hour <= 11);
    if (morningHours.length === 0) return { label: '09:00 - 11:30', val: 0 };
    const maxH = morningHours.reduce((prev, curr) => (curr.calls > prev.calls ? curr : prev), morningHours[0]);
    return { label: `${maxH.hourLabel}`, val: maxH.calls };
  }, [peakTrafficList]);

  const peakAfternoon = useMemo(() => {
    const eveningHours = peakTrafficList.filter((p) => p.hour >= 12 && p.hour <= 23);
    if (eveningHours.length === 0) return { label: '18:00 - 21:00', val: 0 };
    const maxH = eveningHours.reduce((prev, curr) => (curr.calls > prev.calls ? curr : prev), eveningHours[0]);
    return { label: `${maxH.hourLabel}`, val: maxH.calls };
  }, [peakTrafficList]);

  const avgCallsPerHour = useMemo(() => {
    const totalCalls = dashboardData?.kpi.totalCalls || 0;
    return Math.round(totalCalls / 24);
  }, [dashboardData]);

  // ==========================================
  // 5. TOP CLIENTS, INVOICE SETTLEMENT & PRODUCT PORTFOLIO
  // ==========================================
  const topClients = useMemo(() => {
    return dashboardData?.topClients || [];
  }, [dashboardData]);

  const invoiceSettlement = useMemo(() => {
    return dashboardData?.invoiceSettlement;
  }, [dashboardData]);

  const productPortfolio = useMemo(() => {
    return dashboardData?.productPortfolio;
  }, [dashboardData]);

  const productAdoptionList = useMemo(() => {
    return productPortfolio?.productAdoption || [];
  }, [productPortfolio]);

  const activeAdoptionChartItems = useMemo(() => {
    const withActivity = productAdoptionList.filter((p) => p.monthlyRevenue > 0 || p.activeClientsCount > 0);
    return withActivity.length > 0 ? withActivity : productAdoptionList.slice(0, 6);
  }, [productAdoptionList]);

  const revenueMixDonutSeries = useMemo(() => {
    const v = Number(productPortfolio?.voicePct || 0);
    const s = Number(productPortfolio?.subscriptionPct || 0);
    const o = Number(productPortfolio?.oneTimePct || 0);
    if (v + s + o <= 0) return [100, 0, 0];
    return [v, s, o];
  }, [productPortfolio]);

  const revenueMixDonutOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'donut',
        fontFamily: 'Inter, Outfit, sans-serif',
        background: 'transparent',
      },
      colors: ['#163A6B', '#10B981', '#F59E0B'],
      labels: ['Pay-Per-Use (SIP Trunk CDR)', 'Recurring Subscription (MRR)', 'One-Time Project & Setup'],
      dataLabels: { enabled: false },
      stroke: { show: true, width: 2, colors: [isDark ? '#0D1E3D' : '#FFFDF9'] },
      legend: { show: false },
      plotOptions: {
        pie: {
          donut: {
            size: '74%',
            labels: {
              show: true,
              total: {
                show: true,
                showAlways: true,
                label: 'Total Revenue',
                fontSize: '9.5px',
                fontWeight: 600,
                color: isDark ? '#9FB3C8' : '#64748B',
                formatter: () => `Rp ${formatMillions(productPortfolio?.totalCombinedRevenue)}M`,
              },
              value: {
                fontSize: '14px',
                fontWeight: 800,
                offsetY: 4,
                color: isDark ? '#FAF7EE' : '#0A1428',
              },
            },
          },
        },
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        y: { formatter: (val) => `${val}% dari Total Pendapatan` },
      },
    }),
    [isDark, productPortfolio?.totalCombinedRevenue]
  );

  const productAdoptionBarSeries = useMemo(() => {
    return [
      {
        name: 'Revenue Bulanan (Juta Rp)',
        data: activeAdoptionChartItems.map((p) => Number((p.monthlyRevenue / 1_000_000).toFixed(2))),
      },
    ];
  }, [activeAdoptionChartItems]);

  const productAdoptionBarOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        fontFamily: 'Inter, Outfit, sans-serif',
        toolbar: { show: false },
        background: 'transparent',
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: '58%',
          distributed: true,
        },
      },
      colors:
        activeAdoptionChartItems.length > 0
          ? activeAdoptionChartItems.map((p) => p.color || '#163A6B')
          : ['#163A6B'],
      dataLabels: {
        enabled: true,
        formatter: (val: any) => `Rp ${Number(val).toFixed(2)}M`,
        style: {
          fontSize: '10px',
          fontWeight: 700,
          colors: [isDark ? '#FAF7EE' : '#163A6B'],
        },
      },
      legend: { show: false },
      xaxis: {
        categories: activeAdoptionChartItems.map((p) => p.productName),
        labels: {
          formatter: (val: any) => `Rp ${val}M`,
          style: { colors: isDark ? '#D4CEBF' : '#486581', fontSize: '10px', fontWeight: 600 },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: { colors: isDark ? '#FAF7EE' : '#163A6B', fontSize: '11px', fontWeight: 700 },
          maxWidth: 190,
        },
      },
      grid: {
        borderColor: isDark ? 'rgba(250, 247, 238, 0.08)' : 'rgba(44, 81, 103, 0.12)',
        strokeDashArray: 4,
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        y: { formatter: (val) => `Rp ${Number(val).toFixed(2)} Juta` },
      },
    }),
    [isDark, activeAdoptionChartItems]
  );

  const filteredSubscriptionContracts = useMemo(() => {
    const list = productPortfolio?.subscriptionContracts || [];
    if (contractFilter === 'SUBSCRIPTION') {
      return list.filter((c) => c.billingType === 'SUBSCRIPTION');
    }
    if (contractFilter === 'ONE_TIME') {
      return list.filter((c) => c.billingType === 'ONE_TIME');
    }
    if (contractFilter === 'OVERRIDDEN') {
      return list.filter((c) => c.isPriceOverridden);
    }
    return list;
  }, [productPortfolio?.subscriptionContracts, contractFilter]);

  const formatCategoryLabel = (cat: string) => {
    switch ((cat || '').toUpperCase()) {
      case 'VOICE_TRUNK':
        return { label: 'Voice Trunk', badge: 'badge-light-primary' };
      case 'CLOUD_HOSTING':
        return { label: 'Cloud & Infra', badge: 'badge-light-info' };
      case 'SOFTWARE_SAAS':
        return { label: 'Software / SaaS', badge: 'badge-light-success' };
      case 'PROFESSIONAL_SVC':
        return { label: 'Pro Services', badge: 'badge-light-warning' };
      default:
        return { label: cat || 'Layanan', badge: 'badge-light-secondary' };
    }
  };

  return (
    <div className='dribbble-dashboard'>
      {/* WALLBOARD BANNER HEADER */}
      <div className='wallboard-banner-card mb-3'>
        <div className='wallboard-top-row d-flex flex-wrap align-items-center justify-content-between gap-3'>
          <div className='d-flex align-items-center gap-3'>
            <div className='wallboard-icon-box'>
              <KTIcon iconType='outline' iconName='graph-up' className='fs-2 text-white' />
            </div>
            <div>
              <div className='d-flex align-items-center gap-2'>
                <h1 className='wallboard-title mb-0'>DASHBOARD BILLING &amp; TELECOM ANALYTICS</h1>
              </div>
              <p className='wallboard-subtitle mb-0'>
                PBX Automated Call Settlement, Telkom Rate Benchmark &amp; Carrier Trunk Distribution
              </p>
            </div>
          </div>

          <div className='d-flex align-items-center gap-2.5'>
            {/* Refresh Button */}
            <button
              type='button'
              className={`wallboard-refresh-btn btn d-flex align-items-center gap-2 ${isRefreshing || isLoading ? 'is-loading' : ''}`}
              onClick={handleRefresh}
              disabled={isRefreshing || isLoading}
              title='Refresh Dashboard Data'
            >
              <KTIcon
                iconType='outline'
                iconName='arrows-circle'
                className={`fs-4 ${isRefreshing || isLoading ? 'spin-anim' : ''}`}
              />
              <span>{isRefreshing || isLoading ? 'Memuat...' : 'Refresh'}</span>
            </button>
          </div>
        </div>

        {/* Divider / Border */}
        <div className='wallboard-divider my-3'></div>

        {/* Bottom Row: Filter Bar */}
        <div className='wallboard-bottom-row d-flex flex-wrap align-items-center justify-content-between gap-2 gap-md-4'>
          <div className='d-flex flex-wrap align-items-center gap-2 gap-md-4'>
            {/* Periode Tagihan Filter (Bulan & Tahun) */}
            <div className='wallboard-filter-group d-flex align-items-center gap-2'>
              <span className='filter-label d-flex align-items-center'>
                <KTIcon iconType='outline' iconName='calendar-8' className='fs-3 filter-group-icon' />
                <span>Periode Tagihan:</span>
              </span>
              <DatePicker
                selected={moment(activeInvoiceFilter, 'MMMM YYYY').isValid() ? moment(activeInvoiceFilter, 'MMMM YYYY').toDate() : new Date()}
                onChange={handleInvoiceMonthChange}
                dateFormat='MMMM yyyy'
                showMonthYearPicker
                portalId='root'
                popperPlacement='bottom-start'
                customInput={
                  <button
                    type='button'
                    className='filter-pill-badge btn border-0 text-start d-flex align-items-center gap-1'
                    title='Klik untuk memilih periode bulan dan tahun tagihan'
                  >
                    <span>{activeInvoiceFilter}</span>
                    <span className='fs-9 opacity-75 ms-1'>▾</span>
                  </button>
                }
              />
            </div>

            <span className='filter-separator d-none d-md-inline'>|</span>

            {/* Client Filter (Dropdown for Admin, locked display for Client) */}
            <div className='wallboard-filter-group d-flex align-items-center gap-2'>
              <span className='filter-label d-flex align-items-center'>
                <KTIcon iconType='outline' iconName='profile-user' className='fs-3 filter-group-icon' />
                <span>Filter Klien:</span>
              </span>
              {isAdmin ? (
                <div className='min-w-220px'>
                  <AppSelect
                    size='sm'
                    options={clientOptions}
                    value={selectedClientId}
                    onChange={(val) => setSelectedClientId(val || '')}
                    placeholder='Semua Klien (All)'
                    isClearable={false}
                  />
                </div>
              ) : (
                <span className='filter-pill-badge px-3 py-1 fw-bold text-truncate max-w-220px'>
                  {currentUser?.tenant_name || currentUser?.nama || 'Akun Enterprise Klien'}
                </span>
              )}
            </div>
          </div>

          {/* Scope View Switcher (Semua vs Produk & Layanan vs SIP Trunk CDR) */}
          <div className='filter-pills'>
            <button
              type='button'
              className={`pill-btn ${dashboardScope === 'ALL' ? 'active' : ''}`}
              onClick={() => setDashboardScope('ALL')}
            >
              Semua Modul
            </button>
            <button
              type='button'
              className={`pill-btn ${dashboardScope === 'PRODUCTS' ? 'active' : ''}`}
              onClick={() => setDashboardScope('PRODUCTS')}
            >
              Produk &amp; Layanan ({dashboardData?.productPortfolio?.totalCatalogProductsCount || 12})
            </button>
            <button
              type='button'
              className={`pill-btn ${dashboardScope === 'CDR' ? 'active' : ''}`}
              onClick={() => setDashboardScope('CDR')}
            >
              Trafik SIP Trunk
            </button>
          </div>
        </div>
      </div>

      {/* ======================================================== */}
      {/* BAGIAN ANALITIK TRAFIK TELECOM (SIP TRUNK CDR)           */}
      {/* ======================================================== */}
      {(dashboardScope === 'ALL' || dashboardScope === 'CDR') && (
        <>
          {/* 4 HIGH-IMPACT KPI METRIC CARDS (DENGAN KOMPARASI TELKOM) */}
          <div className='dashboard-kpi-grid mb-3'>
        <div className='kpi-stat-card'>
          <div className='kpi-header'>
            <div className='kpi-icon-wrap icon-revenue'>
              <KTIcon iconType='outline' iconName='wallet' className='fs-2 text-white' />
            </div>
            <span className='kpi-badge'>Smart Billing</span>
          </div>
          <div className='kpi-title'>Total Tagihan Smart Billing</div>
          <div className='kpi-value'>
            Rp {formatMillions(dashboardData?.kpi.totalSmartCost)}M
          </div>
          <div className='fs-8 text-muted mt-1'>
            {dashboardData?.kpi.totalSmartCost ? `Rp ${formatNumber(Math.round(dashboardData.kpi.totalSmartCost))}` : 'Akumulasi tagihan per-detik terlaksana'}
          </div>
        </div>

        <div className='kpi-stat-card'>
          <div className='kpi-header'>
            <div className='kpi-icon-wrap icon-traffic'>
              <KTIcon iconType='outline' iconName='chart-line' className='fs-2 text-white' />
            </div>
            <span className='kpi-badge'>Baseline Telkom</span>
          </div>
          <div className='kpi-title'>Estimasi Standar Telkom</div>
          <div className='kpi-value'>
            Rp {formatMillions(dashboardData?.kpi.totalTelkomCost)}M
          </div>
          <div className='fs-8 text-muted mt-1'>
            {dashboardData?.kpi.totalTelkomCost ? `Rp ${formatNumber(Math.round(dashboardData.kpi.totalTelkomCost))}` : 'Biaya jika menggunakan tarif normal Telkom'}
          </div>
        </div>

        <div className='kpi-stat-card'>
          <div className='kpi-header'>
            <div className='kpi-icon-wrap icon-cogs'>
              <KTIcon iconType='outline' iconName='discount' className='fs-2 text-white' />
            </div>
            <span
              className='kpi-badge kpi-badge-savings'
            >
              Hemat {dashboardData?.kpi.savingsPct || 0}% ↗
            </span>
          </div>
          <div className='kpi-title'>Total Penghematan Klien</div>
          <div className='kpi-value text-success'>
            Rp {formatMillions(dashboardData?.kpi.totalSavings)}M
          </div>
          <div className='fs-8 text-muted mt-1'>Efisiensi riil yang dinikmati enterprise</div>
        </div>

        <div className='kpi-stat-card'>
          <div className='kpi-header'>
            <div className='kpi-icon-wrap icon-clients'>
              <KTIcon iconType='outline' iconName='phone' className='fs-2 text-white' />
            </div>
            <span className='kpi-badge'>{formatCalls(dashboardData?.kpi.totalCalls)} Calls</span>
          </div>
          <div className='kpi-title'>Total Durasi Terhitung</div>
          <div className='kpi-value'>{formatCompactMinutes(dashboardData?.kpi.totalMinutes)}</div>
          <div className='fs-8 text-muted mt-1'>Volume trafik panggilan inbound &amp; outbound</div>
        </div>
      </div>

      {/* ======================================================== */}
      {/* KARTU 1: KOMPARASI BIAYA HARIAN (KITA VS TELKOM)         */}
      {/* ======================================================== */}
      <div className='bento-card mb-3 grid-col-full'>
        <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4'>
          <div className='d-flex align-items-center gap-3'>
            <div className='wallboard-icon-box size-40px'>
              <KTIcon iconType='outline' iconName='chart-simple' className='fs-3 text-white' />
            </div>
            <div>
              <h3 className='mb-0 fw-bolder fs-5 text-main-custom'>Komparasi Biaya Harian (Smart Billing vs Telkom PSTN)</h3>
              <p className='mb-0 text-muted-custom fs-7'>
                Perbandingan riil tagihan harian Smart Billing versus baseline Telkom beserta margin efisiensi pada periode {activeInvoiceFilter}
              </p>
            </div>
          </div>

          {/* Segmented Control (Grafik vs Data, Grafik, Data) */}
          <div className='segmented-toggle-group'>
            <button
              type='button'
              className={`segmented-toggle-btn ${comparisonView === 'SPLIT' ? 'active' : ''}`}
              onClick={() => setComparisonView('SPLIT')}
            >
              <KTIcon iconType='outline' iconName='category' />
              <span>Grafik vs Data</span>
            </button>
            <button
              type='button'
              className={`segmented-toggle-btn ${comparisonView === 'CHART' ? 'active' : ''}`}
              onClick={() => setComparisonView('CHART')}
            >
              <KTIcon iconType='outline' iconName='graph-up' />
              <span>Grafik</span>
            </button>
            <button
              type='button'
              className={`segmented-toggle-btn ${comparisonView === 'DATA' ? 'active' : ''}`}
              onClick={() => setComparisonView('DATA')}
            >
              <KTIcon iconType='outline' iconName='element-11' />
              <span>Data</span>
            </button>
          </div>
        </div>

        {/* VIEW 1: GRAFIK VS DATA (SPLIT DUA KOLOM: AREA CHART VS TABEL) */}
        {comparisonView === 'SPLIT' && (
          <div className='row align-items-start g-4'>
            <div className='col-lg-5 col-md-6'>
              <div className='chart-h-220'>
                <Chart options={monthlyAreaOptions} series={monthlyAreaSeries} type='area' height='100%' />
              </div>
            </div>

            <div className='col-lg-7 col-md-6'>
              <div className='table-responsive'>
                <table className='dashboard-split-table'>
                  <thead>
                    <tr>
                      <th>Tanggal</th>
                      <th>Smart Billing</th>
                      <th>Standar Telkom</th>
                      <th>Penghematan</th>
                      <th>Efisiensi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyComparisonData.length === 0 ? (
                      <tr>
                        <td colSpan={5} className='text-center py-4 text-muted'>
                          Tidak ada data panggilan pada rentang periode ini
                        </td>
                      </tr>
                    ) : (
                      monthlyComparisonData.slice(-7).map((item, idx) => (
                        <tr key={idx}>
                          <td className='fw-bold text-main-custom'>{item.month}</td>
                          <td className='fw-bold text-main-custom'>
                            {formatShortIdr(item.smartCost)}
                          </td>
                          <td className='text-muted-custom'>{formatShortIdr(item.telkomCost)}</td>
                          <td className='fw-bolder text-success'>
                            + {formatShortIdr(item.savings)}
                          </td>
                          <td>
                            <span className='badge badge-light-success fw-bolder px-2.5 py-1'>
                              {item.pct}% Hemat
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* VIEW 2: FULL GRAFIK BAR COMPARISON */}
        {comparisonView === 'CHART' && (
          <div>
            <div className='chart-h-320'>
              <Chart options={comparisonBarOptions} series={comparisonBarSeries} type='bar' height='100%' />
            </div>
          </div>
        )}

        {/* VIEW 3: FULL DATA TABLE */}
        {comparisonView === 'DATA' && (
          <div className='table-responsive'>
            <table className='dashboard-split-table'>
              <thead>
                <tr>
                  <th>Tanggal Panggilan</th>
                  <th>Total Panggilan</th>
                  <th>Durasi Terhitung</th>
                  <th>Tagihan Smart Billing</th>
                  <th>Standar Tarif Telkom</th>
                  <th>Nilai Penghematan</th>
                  <th>Persentase Efisiensi</th>
                </tr>
              </thead>
              <tbody>
                {monthlyComparisonData.length === 0 ? (
                  <tr>
                    <td colSpan={7} className='text-center py-4 text-muted'>
                      Tidak ada data panggilan pada rentang periode ini
                    </td>
                  </tr>
                ) : (
                  monthlyComparisonData.map((item, idx) => (
                    <tr key={idx}>
                      <td className='fw-bold text-main-custom'>{item.month}</td>
                      <td>{formatNumber(item.calls)} Panggilan</td>
                      <td>{formatCompactMinutes(item.mins)}</td>
                      <td className='fw-bold text-main-custom'>
                        Rp {formatNumber(Math.round(item.smartCost))}
                      </td>
                      <td className='text-muted-custom'>Rp {formatNumber(Math.round(item.telkomCost))}</td>
                      <td className='fw-bolder text-success'>
                        + Rp {formatNumber(Math.round(item.savings))}
                      </td>
                      <td>
                        <span className='badge badge-light-success fw-bolder px-3 py-1.5'>
                          {item.pct}% Hemat
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ======================================================== */}
      {/* KARTU 2 & 3: DISTRIBUSI PROVIDER & DESTINASI PANGGILAN   */}
      {/* ======================================================== */}
      <div className='row g-3 mb-3'>
        {/* KARTU PROVIDER & TRUNK */}
        <div className='col-lg-6'>
          <div className='bento-card h-100'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4'>
              <div className='d-flex align-items-center gap-3'>
                <div className='wallboard-icon-box size-40px'>
                  <KTIcon iconType='outline' iconName='router' className='fs-3 text-white' />
                </div>
                <div>
                  <h3 className='mb-0 fw-bolder fs-5 text-main-custom'>Distribusi Provider &amp; Trunk</h3>
                  <p className='mb-0 text-muted-custom fs-7'>Porsi traffic, durasi panggilan &amp; alokasi trunk</p>
                </div>
              </div>

              {/* Segmented Control */}
              <div className='segmented-toggle-group'>
                <button
                  type='button'
                  className={`segmented-toggle-btn ${providerView === 'SPLIT' ? 'active' : ''}`}
                  onClick={() => setProviderView('SPLIT')}
                >
                  <KTIcon iconType='outline' iconName='category' />
                  <span>Grafik vs Data</span>
                </button>
                <button
                  type='button'
                  className={`segmented-toggle-btn ${providerView === 'CHART' ? 'active' : ''}`}
                  onClick={() => setProviderView('CHART')}
                >
                  <KTIcon iconType='outline' iconName='graph-up' />
                  <span>Grafik</span>
                </button>
                <button
                  type='button'
                  className={`segmented-toggle-btn ${providerView === 'DATA' ? 'active' : ''}`}
                  onClick={() => setProviderView('DATA')}
                >
                  <KTIcon iconType='outline' iconName='element-11' />
                  <span>Data</span>
                </button>
              </div>
            </div>

            {/* SPLIT VIEW (DONUT CARRIER DISTRIBUTION & TABEL) */}
            {providerView === 'SPLIT' && (
              <div className='d-flex align-items-start gap-3'>
                <div className='d-flex flex-column align-items-center justify-content-start donut-col-130'>
                  <div className='donut-box-130'>
                    <Chart options={providerDonutOptions} series={providerDonutSeries} type='donut' width='100%' height='100%' />
                  </div>
                  <span className='fs-9 text-muted-custom fw-semibold mt-1'>
                    {providerData.length} Trunk Aktif
                  </span>
                </div>

                <div className='flex-grow-1 align-self-start min-w-0'>
                  <div className='table-responsive'>
                    <table className='dashboard-split-table'>
                      <thead>
                        <tr>
                          <th className='w-40'>Provider</th>
                          <th className='w-30 text-end'>Calls</th>
                          <th className='w-30 text-end'>Porsi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {providerData.length === 0 ? (
                          <tr>
                            <td colSpan={3} className='text-center py-3 text-muted'>
                              Belum ada data provider
                            </td>
                          </tr>
                        ) : (
                          providerData.map((item, idx) => (
                            <tr key={idx}>
                              <td>
                                <div className='d-flex align-items-center'>
                                  <span className='status-dot' ref={(el) => { if (el) el.style.backgroundColor = item.color; }}></span>
                                  <span className='fw-bold text-main-custom fs-8 text-truncate' title={item.name}>
                                    {item.name}
                                  </span>
                                </div>
                              </td>
                              <td className='text-end fw-semibold'>{formatCalls(item.calls)}</td>
                              <td className='text-end fw-bold'>{item.porsi.toFixed(1)}%</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* CHART ONLY VIEW: POLAR AREA CARRIER DISTRIBUTION */}
            {providerView === 'CHART' && (
              <div className='d-flex flex-column align-items-center justify-content-center p-3'>
                <div className='donut-box-320'>
                  <Chart options={providerPolarOptions} series={providerPolarSeries} type='polarArea' width='100%' height='100%' />
                </div>
              </div>
            )}

            {/* DATA ONLY VIEW */}
            {providerView === 'DATA' && (
              <div className='table-responsive'>
                <table className='dashboard-split-table'>
                  <thead>
                    <tr>
                      <th>Provider Trunk</th>
                      <th>Volume Panggilan</th>
                      <th>Total Durasi</th>
                      <th>Billed Cost</th>
                      <th>Porsi (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {providerData.length === 0 ? (
                      <tr>
                        <td colSpan={5} className='text-center py-4 text-muted'>
                          Belum ada data provider
                        </td>
                      </tr>
                    ) : (
                      providerData.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            <div className='d-flex align-items-center'>
                              <span className='status-dot' ref={(el) => { if (el) el.style.backgroundColor = item.color; }}></span>
                              <span className='fw-bold text-main-custom'>{item.name}</span>
                            </div>
                          </td>
                          <td className='fw-semibold'>{formatNumber(item.calls)}</td>
                          <td>{formatCompactMinutes(item.durationMins)}</td>
                          <td className='fw-bold text-main-custom'>
                            Rp {formatMillions(item.cost)}M
                          </td>
                          <td className='fw-bolder'>{item.porsi.toFixed(1)}%</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* KARTU DISTRIBUSI DESTINASI (GSM VS PSTN VS SLJJ) */}
        <div className='col-lg-6'>
          <div className='bento-card h-100'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3'>
              <div className='d-flex align-items-center gap-3'>
                <div className='wallboard-icon-box'>
                  <KTIcon iconType='outline' iconName='geolocation' className='fs-3 text-white' />
                </div>
                <div>
                  <h3 className='mb-0 fw-bolder fs-5 text-main-custom'>Distribusi Destinasi Panggilan</h3>
                  <p className='mb-0 text-muted-custom fs-7'>Porsi nomor GSM (08xx), Lokal PSTN, SLJJ &amp; Call Center</p>
                </div>
              </div>

              {/* Toggle Ringkasan vs Data */}
              <div className='segmented-toggle-group'>
                <button
                  type='button'
                  className={`segmented-toggle-btn ${destinationView === 'SUMMARY' ? 'active' : ''}`}
                  onClick={() => setDestinationView('SUMMARY')}
                >
                  <KTIcon iconType='outline' iconName='category' />
                  <span>Ringkasan</span>
                </button>
                <button
                  type='button'
                  className={`segmented-toggle-btn ${destinationView === 'DATA' ? 'active' : ''}`}
                  onClick={() => setDestinationView('DATA')}
                >
                  <KTIcon iconType='outline' iconName='element-11' />
                  <span>Tabel Data</span>
                </button>
              </div>
            </div>

            {destinationView === 'SUMMARY' ? (
              <div>
                {/* Visual Multi-Segment Bar */}
                <div className='destination-bar-wrap'>
                  {destinationData.map((d, i) => (
                    <div
                      key={i}
                      className='dest-bar-item'
                      ref={(el) => { if (el) { el.style.width = `${Math.max(d.porsi, 1)}%`; el.style.backgroundColor = d.color; } }}
                      title={`${d.name}: ${d.porsi}%`}
                    />
                  ))}
                </div>

                {/* List Breakdown */}
                <div className='d-flex flex-column gap-2'>
                  {destinationData.length === 0 ? (
                    <div className='text-center py-4 text-muted fs-8'>Belum ada data destinasi</div>
                  ) : (
                    destinationData.map((item, idx) => (
                      <div
                        key={idx}
                        className='d-flex align-items-center justify-content-between p-2 rounded callout-row-item'
                      >
                        <div className='d-flex align-items-center gap-2'>
                          <span className='status-dot' ref={(el) => { if (el) el.style.backgroundColor = item.color; }} />
                          <div>
                            <div className='fw-bold fs-8 text-main-custom'>{item.name}</div>
                            <div className='fs-9 text-muted'>
                              {formatNumber(item.calls)} Panggilan • {formatCompactMinutes(item.billSec / 60)}
                            </div>
                          </div>
                        </div>
                        <div className='text-end'>
                          <div className='fw-bolder fs-8 text-main-custom'>
                            Rp {formatMillions(item.cost)}M
                          </div>
                          <span className='badge badge-light-primary fs-9 px-1.5 py-0.5 fw-bold'>
                            {item.porsi}%
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <div className='table-responsive'>
                <table className='dashboard-split-table'>
                  <thead>
                    <tr>
                      <th>Kategori Destinasi</th>
                      <th>Prefix</th>
                      <th className='text-end'>Calls</th>
                      <th className='text-end'>Total Biaya</th>
                      <th className='text-end'>Porsi (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {destinationData.length === 0 ? (
                      <tr>
                        <td colSpan={5} className='text-center py-4 text-muted'>Belum ada data destinasi</td>
                      </tr>
                    ) : (
                      destinationData.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            <div className='d-flex align-items-center'>
                              <span className='status-dot' ref={(el) => { if (el) el.style.backgroundColor = item.color; }} />
                              <span className='fw-bold text-main-custom fs-8'>{item.name}</span>
                            </div>
                          </td>
                          <td><span className='badge badge-light-secondary fs-9'>{item.prefix}</span></td>
                          <td className='text-end fw-semibold'>{formatNumber(item.calls)}</td>
                          <td className='text-end fw-bold text-main-custom'>
                            Rp {formatMillions(item.cost)}M
                          </td>
                          <td className='text-end fw-bolder'>{item.porsi}%</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ======================================================== */}
      {/* KARTU JAM SIBUK PANGGILAN 24 JAM (FULL WIDTH BANNER)     */}
      {/* ======================================================== */}
      <div className='bento-card mb-3 grid-col-full'>
        <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-2'>
          <div className='d-flex align-items-center gap-3'>
            <div className='wallboard-icon-box'>
              <KTIcon iconType='outline' iconName='time' className='fs-3 text-white' />
            </div>
            <div>
              <h3 className='mb-0 fw-bolder fs-5 text-main-custom'>Jam Sibuk Panggilan (Peak Hours)</h3>
              <p className='mb-0 text-muted-custom fs-7'>Distribusi volume trafik panggilan 24 jam &amp; utilisasi trunk</p>
            </div>
          </div>
          <span className='badge badge-light-success fw-bold px-2.5 py-1.5 fs-8'>
            Trafik Terpantau Aktif
          </span>
        </div>

        {/* Area Chart 24-Hour */}
        <div className='chart-h-200'>
          <Chart options={peakTrafficOptions} series={peakTrafficSeries} type='area' height='100%' />
        </div>

        {/* Peak Highlights Metric Chips */}
        <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3 pt-2.5 border-top border-secondary border-opacity-10'>
          <div className='d-flex align-items-center gap-2'>
            <span className='badge badge-light-danger fw-bold fs-9'>Puncak Pagi</span>
            <span className='fs-8 fw-bolder text-main-custom'>
              {peakMorning.label} ({formatCalls(peakMorning.val)} calls/jam)
            </span>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <span className='badge badge-light-warning fw-bold fs-9'>Puncak Siang/Malam</span>
            <span className='fs-8 fw-bolder text-main-custom'>
              {peakAfternoon.label} ({formatCalls(peakAfternoon.val)} calls/jam)
            </span>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <span className='badge badge-light-primary fw-bold fs-9'>Rata-rata</span>
            <span className='fs-8 fw-bold text-muted-custom'>
              {avgCallsPerHour.toLocaleString('id-ID')} calls/jam
            </span>
          </div>
        </div>
      </div>
        </>
      )}

      {/* ======================================================== */}
      {/* BAGIAN PORTOFOLIO 12 MASTER PRODUK, SAAS (MRR) & KONTRAK */}
      {/* ======================================================== */}
      {(dashboardScope === 'ALL' || dashboardScope === 'PRODUCTS') && (
        <>
          {/* CHART 1 & 2: KOMPOSISI SUMBER PENDAPATAN (REVENUE MIX) & ADOPSI 12 PRODUK */}
          <div className='row g-3 mb-3'>
            {/* CHART 1: KOMPOSISI SUMBER PENDAPATAN (USAGE VS RECURRING MRR VS ONE-TIME) */}
            <div className='col-lg-5'>
              <div className='bento-card h-100'>
                <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3'>
                  <div className='d-flex align-items-center gap-3'>
                    <div className='wallboard-icon-box size-40px'>
                      <KTIcon iconType='outline' iconName='chart-pie-3' className='fs-3 text-white' />
                    </div>
                    <div>
                      <h3 className='mb-0 fw-bolder fs-5 text-main-custom'>
                        Komposisi Sumber Pendapatan
                      </h3>
                      <p className='mb-0 text-muted-custom fs-7'>
                        Porsi Pay-Per-Use Voice vs Recurring SaaS/Cloud (MRR) vs One-Time
                      </p>
                    </div>
                  </div>
                  <span className='badge badge-light-primary fw-bold px-2.5 py-1 fs-9'>
                    {productPortfolio?.activeProductsUsedCount || 0} / {productPortfolio?.totalCatalogProductsCount || 12} Produk Aktif
                  </span>
                </div>

                {/* Multi-Segment Bar */}
                <div className='destination-bar-wrap mb-3'>
                  <div
                    className='dest-bar-item dest-bar-voice' ref={(el) => { if (el) el.style.width = `${Math.max(productPortfolio?.voicePct || 0, 2)}%`; }}
                    title={`Pay-Per-Use (SIP Trunk CDR): ${productPortfolio?.voicePct || 0}%`}
                  />
                  <div
                    className='dest-bar-item dest-bar-sub' ref={(el) => { if (el) el.style.width = `${Math.max(productPortfolio?.subscriptionPct || 0, 2)}%`; }}
                    title={`Recurring Subscription (MRR): ${productPortfolio?.subscriptionPct || 0}%`}
                  />
                  <div
                    className='dest-bar-item dest-bar-onetime' ref={(el) => { if (el) el.style.width = `${Math.max(productPortfolio?.oneTimePct || 0, 2)}%`; }}
                    title={`One-Time Project & Setup: ${productPortfolio?.oneTimePct || 0}%`}
                  />
                </div>

                <div className='d-flex align-items-center gap-3 flex-wrap flex-sm-nowrap'>
                  {/* Donut Chart */}
                  <div className='donut-col-140 d-flex flex-column align-items-center justify-content-center mx-auto'>
                    <div className='donut-box-140'>
                      <Chart
                        options={revenueMixDonutOptions}
                        series={revenueMixDonutSeries}
                        type='donut'
                        width='100%'
                        height='100%'
                      />
                    </div>
                    <span className='fs-9 text-muted-custom fw-semibold mt-1'>
                      {productPortfolio?.activeSubscriptionsCount || 0} Kontrak Aktif
                    </span>
                  </div>

                  {/* 3 Stream Breakdown */}
                  <div className='flex-grow-1 d-flex flex-column gap-2 w-100'>
                    <div
                      className='d-flex align-items-center justify-content-between p-2.5 rounded portfolio-item-sub'
                    >
                      <div>
                        <div className='fw-bold fs-8 text-main-custom'>
                          🔄 Recurring Subscription (MRR)
                        </div>
                        <div className='fs-9 text-muted'>Cloud PBX, SaaS, Contact Center, CRM</div>
                      </div>
                      <div className='text-end'>
                        <div className='fw-bolder fs-8 text-success'>
                          Rp {formatNumber(Math.round(productPortfolio?.subscriptionMrrRevenue || 0))}
                        </div>
                        <span className='badge badge-light-success fs-9 px-1.5 py-0.5 fw-bold'>
                          {productPortfolio?.subscriptionPct || 0}%
                        </span>
                      </div>
                    </div>

                    <div
                      className='d-flex align-items-center justify-content-between p-2.5 rounded portfolio-item-voice'
                    >
                      <div>
                        <div className='fw-bold fs-8 text-main-custom'>
                          📞 Pay-Per-Use (SIP Trunk Voice)
                        </div>
                        <div className='fs-9 text-muted'>Pemakaian trafik panggilan CDR</div>
                      </div>
                      <div className='text-end'>
                        <div className='fw-bolder fs-8 text-main-custom'>
                          Rp {formatNumber(Math.round(productPortfolio?.voiceUsageRevenue || 0))}
                        </div>
                        <span className='badge badge-light-primary fs-9 px-1.5 py-0.5 fw-bold'>
                          {productPortfolio?.voicePct || 0}%
                        </span>
                      </div>
                    </div>

                    <div
                      className='d-flex align-items-center justify-content-between p-2.5 rounded portfolio-item-onetime'
                    >
                      <div>
                        <div className='fw-bold fs-8 text-main-custom'>
                          🛠️ One-Time Project &amp; Setup
                        </div>
                        <div className='fs-9 text-muted'>System Integrator &amp; Software Dev</div>
                      </div>
                      <div className='text-end'>
                        <div className='fw-bolder fs-8 text-warning'>
                          Rp {formatNumber(Math.round(productPortfolio?.oneTimeServiceRevenue || 0))}
                        </div>
                        <span className='badge badge-light-warning fs-9 px-1.5 py-0.5 fw-bold'>
                          {productPortfolio?.oneTimePct || 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Portfolio Highlights Footer */}
                <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3 pt-2.5 border-top border-secondary border-opacity-10'>
                  <span className='fs-9 text-muted'>
                    ⚡ Override Harga:{' '}
                    <strong className='text-warning'>
                      {productPortfolio?.customOverrideContractsCount || 0} Kontrak
                    </strong>
                  </span>
                  <span className='fs-9 text-muted'>
                    ⏳ Segera Berakhir:{' '}
                    <strong className='text-danger'>
                      {productPortfolio?.expiringSoonCount || 0} Kontrak
                    </strong>
                  </span>
                  <span className='fs-9 text-muted'>
                    🧾 One-Time Belum Ditagih:{' '}
                    <strong className='text-primary'>
                      {productPortfolio?.unbilledOneTimeCount || 0} Proyek
                    </strong>
                  </span>
                </div>
              </div>
            </div>

            {/* CHART 2: PERFORMA & ADOPSI 12 MASTER PRODUK (LEADERBOARD) */}
            <div className='col-lg-7'>
              <div className='bento-card h-100'>
                <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3'>
                  <div className='d-flex align-items-center gap-3'>
                    <div className='wallboard-icon-box size-40px'>
                      <KTIcon iconType='outline' iconName='element-11' className='fs-3 text-white' />
                    </div>
                    <div>
                      <h3 className='mb-0 fw-bolder fs-5 text-main-custom'>
                        Performa &amp; Adopsi 12 Master Produk
                      </h3>
                      <p className='mb-0 text-muted-custom fs-7'>
                        Kontribusi revenue, jumlah klien berlangganan, dan lisensi/unit terpasang per produk
                      </p>
                    </div>
                  </div>

                  {/* Segmented Control */}
                  <div className='segmented-toggle-group'>
                    <button
                      type='button'
                      className={`segmented-toggle-btn ${productAdoptionView === 'SPLIT' ? 'active' : ''}`}
                      onClick={() => setProductAdoptionView('SPLIT')}
                    >
                      <KTIcon iconType='outline' iconName='category' />
                      <span>Grafik vs Data</span>
                    </button>
                    <button
                      type='button'
                      className={`segmented-toggle-btn ${productAdoptionView === 'CHART' ? 'active' : ''}`}
                      onClick={() => setProductAdoptionView('CHART')}
                    >
                      <KTIcon iconType='outline' iconName='graph-up' />
                      <span>Grafik</span>
                    </button>
                    <button
                      type='button'
                      className={`segmented-toggle-btn ${productAdoptionView === 'DATA' ? 'active' : ''}`}
                      onClick={() => setProductAdoptionView('DATA')}
                    >
                      <KTIcon iconType='outline' iconName='element-11' />
                      <span> Semua (12)</span>
                    </button>
                  </div>
                </div>

                {productAdoptionView === 'SPLIT' && (
                  <div className='row align-items-start g-3'>
                    <div className='col-md-5'>
                      <div className='chart-h-235'>
                        <Chart
                          options={productAdoptionBarOptions}
                          series={productAdoptionBarSeries}
                          type='bar'
                          height='100%'
                        />
                      </div>
                    </div>
                    <div className='col-md-7'>
                      <div className='table-responsive table-scroll-h235'>
                        <table className='dashboard-split-table'>
                          <thead>
                            <tr>
                              <th>Nama Produk</th>
                              <th className='text-center'>Klien</th>
                              <th className='text-center'>Unit</th>
                              <th className='text-end'>Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {productAdoptionList.map((prod) => {
                              const catMeta = formatCategoryLabel(prod.category);
                              return (
                                <tr key={prod.productId}>
                                  <td>
                                    <div className='d-flex flex-column'>
                                      <span className='fw-bold text-main-custom fs-8'>
                                        {prod.productName}
                                      </span>
                                      <div className='d-flex align-items-center gap-1 mt-0.5'>
                                        <span className={`badge ${catMeta.badge} fs-9 py-0.5 px-1.5`}>
                                          {catMeta.label}
                                        </span>
                                        {prod.overrideClientsCount > 0 && (
                                          <span className='badge badge-light-warning fs-9 py-0.5 px-1.5'>
                                            ⚡ {prod.overrideClientsCount} Override
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className='text-center fw-bold'>
                                    {prod.activeClientsCount > 0 ? (
                                      <span className='badge badge-light-primary fw-bolder fs-9'>
                                        {prod.activeClientsCount} Klien
                                      </span>
                                    ) : (
                                      <span className='text-muted fs-9'>0</span>
                                    )}
                                  </td>
                                  <td className='text-center fs-8 text-muted'>
                                    {prod.totalQuantity > 0 ? `${prod.totalQuantity} ${prod.unitName}` : '-'}
                                  </td>
                                  <td className={`text-end fw-bolder ${prod.monthlyRevenue > 0 ? 'text-main-custom' : 'text-muted'}`}>
                                    {prod.monthlyRevenue > 0
                                      ? `Rp ${formatNumber(Math.round(prod.monthlyRevenue))}`
                                      : 'Rp 0'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {productAdoptionView === 'CHART' && (
                  <div className='chart-h-260'>
                    <Chart
                      options={productAdoptionBarOptions}
                      series={productAdoptionBarSeries}
                      type='bar'
                      height='100%'
                    />
                  </div>
                )}

                {productAdoptionView === 'DATA' && (
                  <div className='table-responsive table-scroll-h265'>
                    <table className='dashboard-split-table'>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Produk &amp; Kode</th>
                          <th>Kategori</th>
                          <th>Tipe Tagihan</th>
                          <th className='text-center'>Klien Aktif</th>
                          <th className='text-center'>Total Unit</th>
                          <th className='text-end'>Harga Standar</th>
                          <th className='text-end'>Total Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productAdoptionList.map((prod, idx) => {
                          const catMeta = formatCategoryLabel(prod.category);
                          return (
                            <tr key={prod.productId}>
                              <td className='text-muted fw-bold'>{idx + 1}</td>
                              <td>
                                <div className='fw-bold text-main-custom fs-8'>{prod.productName}</div>
                                <div className='fs-9 text-muted font-monospace'>{prod.productCode}</div>
                              </td>
                              <td>
                                <span className={`badge ${catMeta.badge} fs-9 w-fit-content`}>{catMeta.label}</span>
                              </td>
                              <td>
                                <span className='badge badge-light-secondary fs-9'>{prod.billingType}</span>
                              </td>
                              <td className='text-center fw-bold'>
                                {prod.activeClientsCount} Klien
                                {prod.overrideClientsCount > 0 && (
                                  <span className='badge badge-light-warning ms-1 fs-9'>
                                    ⚡ {prod.overrideClientsCount}
                                  </span>
                                )}
                              </td>
                              <td className='text-center'>
                                {prod.totalQuantity} {prod.unitName}
                              </td>
                              <td className='text-end text-muted'>
                                {prod.billingType === 'PAY_PER_USE'
                                  ? 'Sesuai Rute CDR'
                                  : `Rp ${formatNumber(Math.round(prod.basePrice))}`}
                              </td>
                              <td className='text-end fw-bolder text-main-custom'>
                                Rp {formatNumber(Math.round(prod.monthlyRevenue))} ({prod.revenueSharePct}%)
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* KARTU 3: MONITORING KONTRAK LANGGANAN, MASA AKTIF & LAYANAN ONE-TIME */}
          <div className='bento-card mb-3'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3'>
              <div className='d-flex align-items-center gap-3'>
                <div className='wallboard-icon-box size-40px'>
                  <KTIcon iconType='outline' iconName='shield-tick' className='fs-3 text-white' />
                </div>
                <div>
                  <h3 className='mb-0 fw-bolder fs-5 text-main-custom'>
                    Monitoring Kontrak Langganan, Lisensi &amp; Layanan Klien
                  </h3>
                  <p className='mb-0 text-muted-custom fs-7'>
                    Pelacakan produk aktif per klien, harga override vs master, masa berlaku kontrak, serta status tagihan One-Time
                  </p>
                </div>
              </div>

              {/* Filter Pills */}
              <div className='segmented-toggle-group'>
                <button
                  type='button'
                  className={`segmented-toggle-btn ${contractFilter === 'ALL' ? 'active' : ''}`}
                  onClick={() => setContractFilter('ALL')}
                >
                  <span>Semua ({productPortfolio?.subscriptionContracts?.length || 0})</span>
                </button>
                <button
                  type='button'
                  className={`segmented-toggle-btn ${contractFilter === 'SUBSCRIPTION' ? 'active' : ''}`}
                  onClick={() => setContractFilter('SUBSCRIPTION')}
                >
                  <span>
                    🔄 Recurring MRR (
                    {(productPortfolio?.subscriptionContracts || []).filter(
                      (c) => c.billingType === 'SUBSCRIPTION'
                    ).length}
                    )
                  </span>
                </button>
                <button
                  type='button'
                  className={`segmented-toggle-btn ${contractFilter === 'ONE_TIME' ? 'active' : ''}`}
                  onClick={() => setContractFilter('ONE_TIME')}
                >
                  <span>
                    🛠️ One-Time (
                    {(productPortfolio?.subscriptionContracts || []).filter(
                      (c) => c.billingType === 'ONE_TIME'
                    ).length}
                    )
                  </span>
                </button>
                <button
                  type='button'
                  className={`segmented-toggle-btn ${contractFilter === 'OVERRIDDEN' ? 'active' : ''}`}
                  onClick={() => setContractFilter('OVERRIDDEN')}
                >
                  <span>
                    ⚡ Harga Override (
                    {(productPortfolio?.subscriptionContracts || []).filter((c) => c.isPriceOverridden)
                      .length}
                    )
                  </span>
                </button>
              </div>
            </div>

            <div className='table-responsive'>
              <table className='dashboard-split-table'>
                <thead>
                  <tr>
                    <th>Klien Enterprise</th>
                    <th>Produk &amp; Layanan</th>
                    <th>Kategori &amp; Tipe</th>
                    <th className='text-center'>Kuantitas / Lisensi</th>
                    <th className='text-end'>Skema Harga (Master vs Klien)</th>
                    <th>Masa Berlaku Kontrak</th>
                    <th className='text-center'>Status Kontrak / Tagihan</th>
                    <th className='text-end'>Nilai Tagihan / Bulan</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubscriptionContracts.length === 0 ? (
                    <tr>
                      <td colSpan={8} className='text-center py-4 text-muted'>
                        Tidak ada kontrak langganan yang sesuai dengan filter ini
                      </td>
                    </tr>
                  ) : (
                    filteredSubscriptionContracts.map((item) => {
                      const catMeta = formatCategoryLabel(item.category);
                      return (
                        <tr key={item.subscriptionId} className={item.isPriceOverridden ? 'rate-row-overridden' : ''}>
                          <td>
                            <div className='fw-bold text-main-custom fs-8'>{item.clientName}</div>
                            <div className='fs-9 text-muted'>Tenant ID #{item.clientId}</div>
                          </td>
                          <td>
                            <div className='fw-bold text-main-custom fs-8'>{item.productName}</div>
                            <div className='fs-9 text-muted font-monospace'>{item.productCode}</div>
                          </td>
                          <td>
                            <div className='d-flex flex-column gap-1'>
                              <span
                                className={`badge ${catMeta.badge} fs-9 w-fit-content`}
                              >
                                {catMeta.label}
                              </span>
                              <span className='fs-9 text-muted'>{item.billingType}</span>
                            </div>
                          </td>
                          <td className='text-center fw-bold'>
                            {item.billingType === 'PAY_PER_USE' ? (
                              <span className='badge badge-light-primary fs-9'>Usage CDR</span>
                            ) : (
                              `${item.quantity} ${item.unitName}`
                            )}
                          </td>
                          <td className='text-end'>
                            {item.billingType === 'PAY_PER_USE' ? (
                              item.isPriceOverridden ? (
                                <span className='badge badge-warning text-dark fw-bolder fs-9'>
                                  ⚡ Custom Override Rute CDR
                                </span>
                              ) : (
                                <span className='badge badge-light-secondary text-muted fs-9'>
                                  🛡️ Standar Master Rates
                                </span>
                              )
                            ) : (
                              <div className='d-flex flex-column align-items-end'>
                                <span className='fw-bold fs-8 text-main-custom'>
                                  Rp {formatNumber(Math.round(item.unitPrice))} / {item.unitName}
                                </span>
                                {item.isPriceOverridden ? (
                                  <span className='badge badge-warning text-dark fw-bold fs-9 mt-0.5'>
                                    ⚡ Override (Master: Rp {formatNumber(Math.round(item.basePrice))})
                                  </span>
                                ) : (
                                  <span className='fs-9 text-muted'>🛡️ Harga Master Standar</span>
                                )}
                              </div>
                            )}
                          </td>
                          <td>
                            <div className='fs-8 fw-semibold text-main-custom'>
                              {item.startDate || '-'} s/d {item.endDate || 'Berjalan (Auto-Renew)'}
                            </div>
                            {item.daysRemaining !== null && item.daysRemaining !== undefined && (
                              <div
                                className={`fs-9 fw-bold ${
                                  item.daysRemaining <= 60 ? 'text-danger' : 'text-success'
                                }`}
                              >
                                {item.daysRemaining < 0
                                  ? `Lewat ${Math.abs(item.daysRemaining)} hari`
                                  : `Sisa ${item.daysRemaining} hari aktif`}
                              </div>
                            )}
                          </td>
                          <td className='text-center'>
                            {item.healthStatus === 'UNBILLED_ONE_TIME' ? (
                              <span className='badge badge-light-warning fw-bolder fs-9 px-2.5 py-1'>
                                ⏳ Belum Ditagih (Unbilled)
                              </span>
                            ) : item.healthStatus === 'INVOICED_ONE_TIME' ? (
                              <span className='badge badge-light-success fw-bolder fs-9 px-2.5 py-1'>
                                ✓ Sudah Ditagihkan
                              </span>
                            ) : item.healthStatus === 'EXPIRING_SOON' ? (
                              <span className='badge badge-light-danger fw-bolder fs-9 px-2.5 py-1'>
                                ⚠️ Segera Berakhir
                              </span>
                            ) : (
                              <span className='badge badge-light-success fw-bolder fs-9 px-2.5 py-1'>
                                ● Kontrak Aktif
                              </span>
                            )}
                          </td>
                          <td className='text-end fw-bolder text-main-custom'>
                            Rp {formatNumber(Math.round(item.totalValue))}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ======================================================== */}
      {/* TOP CLIENTS & RECENT INVOICES (COMPACT & HEMAT TEMPAT)   */}
      {/* ======================================================== */}
      <div className='row g-3'>
        {/* Top Clients Table */}
        <div className='col-lg-7'>
          <div className='bento-card h-100 p-3 p-xl-4'>
            <div className='d-flex align-items-center justify-content-between mb-2.5'>
              <div className='d-flex align-items-center gap-2'>
                <KTIcon iconType='outline' iconName='briefcase' className='fs-4 text-primary' />
                <span className='fw-bolder fs-6 text-main-custom'>Top Performing Enterprise Clients</span>
              </div>
              <span className='badge badge-light-primary fw-bold px-2 py-0.5 fs-9'>
                {topClients.length} Klien Terdaftar
              </span>
            </div>

            <div className='table-responsive'>
              <table className='dashboard-compact-table'>
                <thead>
                  <tr>
                    <th>Enterprise Client</th>
                    <th>Calls</th>
                    <th>Durasi</th>
                    <th className='text-end'>Revenue Tagihan</th>
                  </tr>
                </thead>
                <tbody>
                  {topClients.length === 0 ? (
                    <tr>
                      <td colSpan={4} className='text-center py-4 text-muted'>Belum ada data klien terhitung</td>
                    </tr>
                  ) : (
                    topClients.map((client, idx) => {
                      const initials = client.clientName
                        .split(' ')
                        .map((w) => w[0])
                        .slice(0, 2)
                        .join('')
                        .toUpperCase() || 'CL';
                      return (
                        <tr key={client.clientId || idx}>
                          <td>
                            <div className='d-flex align-items-center'>
                              <div className={`client-avatar-sm ${getAvatarClass(idx)}`}>{initials}</div>
                              <div>
                                <div className='fw-bold fs-8 text-main-custom'>{client.clientName}</div>
                                <div className='fs-9 text-muted'>Enterprise Account #{client.clientId}</div>
                              </div>
                            </div>
                          </td>
                          <td className='fw-semibold'>{formatCalls(client.calls)}</td>
                          <td>{formatCompactMinutes(client.durationMins)}</td>
                          <td className='text-end fw-bolder text-main-custom'>
                            Rp {formatMillions(client.cost)}M
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Recent Invoices Statement */}
        <div className='col-lg-5'>
          <div className='bento-card h-100 card-activity p-3 p-xl-4'>
            <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2.5'>
              <div className='d-flex align-items-center gap-2'>
                <KTIcon iconType='outline' iconName='document' className='fs-4 text-success' />
                <span className='fw-bolder fs-6 text-main-custom'>Aktivitas Settlement Invoice</span>
              </div>
              <div className='d-flex align-items-center gap-1.5'>
                <span className='badge badge-light-primary fw-bold px-2 py-0.5 fs-9'>
                  📅 {activeInvoiceFilter}
                </span>
                <span className='badge badge-light-success fw-bold px-2 py-0.5 fs-9'>Realtime</span>
              </div>
            </div>

            {/* INVOICE SETTLEMENT SUMMARY (PAID, UNPAID, DRAFT) */}
            <div className='invoice-aging-grid'>
              <div
                className='aging-pill aging-paid'
                title={`Total Lunas: Rp ${formatNumber(Math.round(invoiceSettlement?.totalPaid || 0))}`}
              >
                <div className='fs-9 fw-bold text-success'>Lunas (PAID)</div>
                <div className='fw-bolder fs-8 text-main-custom'>
                  Rp {formatMillions(invoiceSettlement?.totalPaid)}M
                </div>
                <div className='fs-9 text-muted'>
                  {invoiceSettlement?.paidPct || 0}% ({(invoiceSettlement?.recentInvoices || []).filter((i) => (i.status || '').toUpperCase() === 'PAID').length} Klien)
                </div>
              </div>
              <div
                className='aging-pill aging-pending'
                title={`Total Belum Lunas: Rp ${formatNumber(Math.round(invoiceSettlement?.totalPending || 0))}`}
              >
                <div className='fs-9 fw-bold text-danger'>Belum Lunas (UNPAID)</div>
                <div className='fw-bolder fs-8 text-main-custom'>
                  Rp {formatMillions(invoiceSettlement?.totalPending)}M
                </div>
                <div className='fs-9 text-muted'>
                  {invoiceSettlement?.pendingPct || 0}% ({(invoiceSettlement?.recentInvoices || []).filter((i) => (i.status || '').toUpperCase() === 'UNPAID').length} Klien)
                </div>
              </div>
              <div
                className='aging-pill aging-overdue'
                title={`Total Draft: Rp ${formatNumber(Math.round(invoiceSettlement?.totalOverdue || 0))}`}
              >
                <div className='fs-9 fw-bold text-muted'>Draft (DRAFT)</div>
                <div className='fw-bolder fs-8 text-main-custom'>
                  Rp {formatMillions(invoiceSettlement?.totalOverdue)}M
                </div>
                <div className='fs-9 text-muted'>
                  {invoiceSettlement?.overduePct || 0}% ({(invoiceSettlement?.recentInvoices || []).filter((i) => (i.status || '').toUpperCase() === 'DRAFT').length} Klien)
                </div>
              </div>
            </div>

            {/* Visual Aging Progress Bar */}
            <div className='progress h-5px mb-3 rounded-1'>
              <div
                className='progress-bar bg-success'
                ref={(el) => { if (el) el.style.width = `${invoiceSettlement?.paidPct || 0}%`; }}
                title={`Lunas (PAID) ${invoiceSettlement?.paidPct || 0}%`}
              />
              <div
                className='progress-bar bg-danger'
                ref={(el) => { if (el) el.style.width = `${invoiceSettlement?.pendingPct || 0}%`; }}
                title={`Belum Lunas (UNPAID) ${invoiceSettlement?.pendingPct || 0}%`}
              />
              <div
                className='progress-bar bg-secondary'
                ref={(el) => { if (el) el.style.width = `${invoiceSettlement?.overduePct || 0}%`; }}
                title={`Draft (DRAFT) ${invoiceSettlement?.overduePct || 0}%`}
              />
            </div>

            <div className='activity-list'>
              {!invoiceSettlement?.recentInvoices || invoiceSettlement.recentInvoices.length === 0 ? (
                <div className='text-center py-4 text-muted fs-8'>Belum ada invoice diterbitkan</div>
              ) : (
                invoiceSettlement.recentInvoices.map((inv, idx) => {
                  const statusNormalized = (inv.status || 'UNPAID').toUpperCase()
                  const isPaid = statusNormalized === 'PAID'
                  const isDraft = statusNormalized === 'DRAFT'
                  const statusClass = isPaid ? 'status-paid' : isDraft ? 'status-draft' : 'status-pending'
                  const statusIcon = isPaid ? '✓' : isDraft ? '✎' : '!'
                  const statusBadgeClass = isPaid ? 'badge-light-success' : isDraft ? 'badge-light-warning' : 'badge-light-danger'
                  const statusLabel = isPaid ? 'PAID' : isDraft ? 'DRAFT' : 'UNPAID'

                  return (
                    <div className='activity-item' key={idx}>
                      <div className={`activity-icon ${statusClass}`}>
                        {statusIcon}
                      </div>
                      <div className='activity-details flex-grow-1'>
                        <div className='d-flex align-items-center gap-2 flex-wrap'>
                          <span className='fw-bold fs-8 text-main-custom'>
                            {isDraft ? '⚡ DRAFT / BULAN BERJALAN' : inv.invoiceNo}
                          </span>
                          <span className={`badge ${statusBadgeClass} fs-9 py-0.5 px-1.5 fw-bold`}>
                            {statusLabel}
                          </span>
                        </div>
                        <div className='fs-9 text-muted'>
                          {inv.clientName} • {inv.period}
                        </div>
                      </div>
                      <div className='text-end'>
                        <div
                          className={`activity-amount ${isPaid ? 'paid' : isDraft ? 'text-muted' : 'pending'} fw-bolder fs-8`}
                        >
                          Rp {formatNumber(Math.round(inv.amount))}
                        </div>
                        <div className='fs-9 text-muted'>
                          ~ Rp {formatMillions(inv.amount)}M
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { DashboardBilling };